import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Package,
  ArrowRight,
  FolderTree,
  Utensils,
  ChevronRight,
  Shield,
  Clock,
  Star,
  Loader2,
  SprayCan,
} from "lucide-react";
import { productService } from "../api/services/productService";

// Helper function to get full image URL
const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (path.startsWith("/uploads")) {
    const apiBaseUrl =
      import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
    return `${apiBaseUrl}${path}`;
  }
  return path;
};

// Product Image Component
const ProductImage = ({ product, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadImage = () => {
      // mainImagePath
      if (product?.mainImagePath) {
        setImageUrl(getFullImageUrl(product.mainImagePath));
        setImageLoading(false);
        return;
      }

      // gallery images path
      if (product?.galleryImages && product.galleryImages.length > 0) {
        const firstImage = product.galleryImages[0];
        if (firstImage?.path) {
          setImageUrl(getFullImageUrl(firstImage.path));
          setImageLoading(false);
          return;
        }
        if (firstImage?.data) {
          setImageUrl(`data:${firstImage.mimeType};base64,${firstImage.data}`);
          setImageLoading(false);
          return;
        }
      }

      // Image URL
      if (product?.image) {
        setImageUrl(product.image);
        setImageLoading(false);
        return;
      }

      setImageLoading(false);
    };

    loadImage();
  }, [product]);

  if (imageLoading && !imageError) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={20} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={product?.imageAlt || product?.name || "Product"}
        className={`object-cover ${className}`}
        onError={() => {
          console.error(`Failed to load product image: ${imageUrl}`);
          setImageError(true);
          setImageLoading(false);
        }}
      />
    );
  }

  return (
    <div
      className={`flex items-center justify-center ${className} bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900`}
    >
      <Package size={24} className="text-gray-400" />
    </div>
  );
};

// ========== CATEGORY CARD==========
const CategoryCard = ({
  title,
  description,
  icon: Icon,
  link,
  delay = 0,
  category,
}) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  useEffect(() => {
    fetchProducts();
  }, [category]);

  useEffect(() => {
    if (products.length > 0) {
      const interval = setInterval(() => {
        setCurrentProductIndex(
          (prev) => (prev + 1) % Math.min(products.length, 5),
        );
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getProductsByCategory(category);

      let productList = [];
      if (response?.data?.products) {
        productList = response.data.products;
      } else if (response?.products) {
        productList = response.products;
      } else if (Array.isArray(response?.data)) {
        productList = response.data;
      } else if (Array.isArray(response)) {
        productList = response;
      }

      setProducts(productList.slice(0, 6));
    } catch (error) {
      console.error(`Error fetching ${category} products:`, error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (productSlug) => {
    navigate(`/products/${productSlug}`);
  };

  const currentProduct = products[currentProductIndex];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, type: "spring", stiffness: 100 }}
      whileHover={{ y: -6 }}
      className="group relative rounded-2xl overflow-hidden bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border border-white/50 dark:border-gray-800/50 shadow-xl"
    >
      {/* Gradient Accent Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-secondary-500" />

      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-500/10 dark:bg-primary-500/20 rounded-full">
            <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wide">
              {title}
            </span>
          </div>
        </div>

        {/* Title & Description */}
        <Link to={`/categories/${category}`}>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer">
            {title}
          </h3>
        </Link>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">
          {description}
        </p>

        {/* Live Product Preview */}
        <div className="mb-5 bg-gray-100/50 dark:bg-gray-800/50 rounded-xl p-4">
          {loading ? (
            <div className="flex items-center justify-center h-16">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : currentProduct ? (
            <button
              onClick={() => handleProductClick(currentProduct.slug)}
              className="w-full flex items-center gap-3 group/product transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-200/70 hover:to-gray-100/70 dark:hover:from-gray-700/70 dark:hover:to-gray-800/70 rounded-xl p-2 -m-2"
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 flex-shrink-0">
                <ProductImage
                  product={currentProduct}
                  className="w-full h-full"
                />
              </div>
              <div className="flex-1 min-w-0 text-left">
                {/* Product Title - Clickable */}
                <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1 group-hover/product:text-primary-600 dark:group-hover/product:text-primary-400 transition-colors">
                  {currentProduct.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {currentProduct.category === "food"
                    ? "Food & Beverages"
                    : "Non-Food Products"}{" "}
                  {/* • SKU: {currentProduct.sku} */}
                </p>
              </div>
              {/* Chevron Arrow - Also Clickable (same button handles both) */}
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 group-hover/product:translate-x-1 group-hover/product:text-primary-500 transition-all duration-300" />
            </button>
          ) : (
            <p className="text-sm text-gray-500 text-center">
              No products available
            </p>
          )}
        </div>

        {/* CTA Button */}
        <Link to={link}>
          <button className="w-full py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]">
            <span>Browse {title}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>
    </motion.div>
  );
};

// ========== CATEGORIES COMPONENT ==========
const Categories = () => {
  const [activeTab, setActiveTab] = useState("featured");

  const categories = [
    {
      id: "food",
      title: "Food & Beverages",
      description:
        "Premium quality food products sourced globally from trusted suppliers. From fresh produce to packaged goods.",
      icon: Utensils,
      link: "/categories/food",
      category: "food",
      gradient: "from-amber-500 to-orange-500",
    },
    {
      id: "non-food",
      title: "Non-Food Products",
      description:
        "A wide range of non-food items including household essentials, personal care products, and more.",
      icon: SprayCan,
      link: "/categories/non-food",
      category: "non-food",
      gradient: "from-blue-500 to-cyan-500",
    },
  ];

  const stats = [
    { value: "98%", label: "Satisfaction Rate", icon: Star },
    { value: "24/7", label: "Support Available", icon: Clock },
    { value: "100%", label: "Quality Assured", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Hero Section */}
      <section className="relative pt-20 lg:pt-28 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 shadow-sm mb-4 sm:mb-6">
                <FolderTree className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  Product Categories
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 sm:mb-6">
                <span className="text-gray-900 dark:text-white">
                  Browse Our
                </span>
                <br />
                <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  Product Categories
                </span>
              </h1>

              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
                Discover our curated selection of high-quality products from
                trusted suppliers worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {categories.map((category, idx) => (
              <CategoryCard
                key={category.id}
                title={category.title}
                description={category.description}
                icon={category.icon}
                link={category.link}
                category={category.category}
                delay={idx * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-4xl mx-auto">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-white/30 dark:border-gray-800/30"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto rounded-lg sm:rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-2">
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                    {stat.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
              <div className="relative p-6 sm:p-8 md:p-10 lg:p-12 text-center text-white">
                <div className="absolute inset-0 bg-black/20" />
                <div className="relative">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                    Ready to Start Sourcing?
                  </h2>
                  <p className="text-sm sm:text-base text-white/90 mb-6 sm:mb-8 max-w-md mx-auto">
                    Contact our team to discuss your product requirements and
                    get started.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 sm:px-8 sm:py-3 bg-white text-primary-600 rounded-xl sm:rounded-2xl font-semibold hover:shadow-xl transition-all hover:scale-105 text-sm sm:text-base"
                    >
                      Contact Sales
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/products"
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 sm:px-8 sm:py-3 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl font-semibold hover:bg-white/30 transition-all border border-white/30 text-sm sm:text-base"
                    >
                      View All Products
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Categories;
